import app from 'flarum/app';
import Switch from 'flarum/components/Switch';
import Button from 'flarum/components/Button';
import icon from 'flarum/helpers/icon';
import Group from 'flarum/models/Group';
import FieldList from '../fields/FieldList';
import ValueHistory from '../utils/ValueHistory';
import AbstractFormPage from './AbstractFormPage';
import SaveControls from '../components/SaveControls';
import FormGroup from '../components/FormGroup';
import RichTextField from '../components/RichTextField';
import PageContainer from '../components/PageContainer';
import SelectWithDisabled from '../components/SelectWithDisabled';
import AutomaticDiscussionOptionsModal from '../modals/AutomaticDiscussionOptionsModal';
import ConfirmationEmailOptionsModal from '../modals/ConfirmationEmailOptionsModal';
import FormEditHeader from '../components/FormEditHeader';
import PermissionDropdown from '../components/PermissionDropdown';

/* global m */

const translationPrefix = 'kilowhat-formulaire.forum.form-edit.';

export default class FormEditPage extends AbstractFormPage {
    init() {
        super.init();

        this.tab = 'template';

        // Used to navigate to a tab directly from another page
        if (app.cache.formulaireFormEditTab) {
            this.tab = app.cache.formulaireFormEditTab;
            delete app.cache.formulaireFormEditTab;
        }
    }

    show(form) {
        // Create a copy of the model
        // That way if we leave the page without saving, the data isn't persisted in the store
        this.form = app.store.createRecord('formulaire-forms', form.copyData());
        this.form.exists = true;

        this.history = new ValueHistory(form.template());
    }

    config(isInitialized, context) {
        super.config(isInitialized, context);

        if (isInitialized) return;

        // Handles quitting the page by navigating/closing
        this.windowOnbeforeunload = event => {
            if (this.dirty) {
                event.preventDefault();
                event.returnValue = app.translator.trans(translationPrefix + 'exiting-with-unsaved-data');
            }
        }

        window.addEventListener('beforeunload', this.windowOnbeforeunload);
    }

    onunload(event) {
        // Handles quitting the page by navigating inside the single page app
        if (this.dirty && !confirm(app.translator.trans(translationPrefix + 'exiting-with-unsaved-data'))) {
            event.preventDefault();
            return;
        }

        window.removeEventListener('beforeunload', this.windowOnbeforeunload);
    }

    content() {
        if (!this.form.attribute('canEdit')) {
            return PageContainer.component('Cannot edit');//TODO: translation
        }

        const warnings = this.warnings();

        return PageContainer.component({}, [
            FormEditHeader.component({
                form: this.form,
                tab: this.tab,
                changeTab: key => {
                    this.tab = key;
                },
                editable: true,
                updateAttribute: this.updateAttribute.bind(this),
                validationErrors: this.validationErrors,
            }),
            warnings.map(warning => m('.Alert.Alert--warning', warning)),
            m('.FormulaireEditFormTab', m('div', {
                key: this.tab, // Force full redraw on tab change
            }, this[this.tab + 'Tab']())),
            SaveControls.component({
                dirty: this.dirty,
                loading: this.loading,
                savedAt: this.savedAt,
                inError: Object.keys(this.validationErrors).length,
                history: this.history,
                onsave: () => {
                    this.loading = true;

                    this.form.save({
                        link_type: this.form.link_type(),
                        link_id: this.form.link_id(),
                        slug: this.form.slug(),
                        title: this.form.title(),
                        private_title: this.form.private_title(),
                        template: this.history.value,
                        accept_submissions: this.form.accept_submissions(),
                        allow_modification: this.form.allow_modification(),
                        max_submissions: this.form.max_submissions(),
                        send_confirmation_to_participants: this.form.send_confirmation_to_participants(),
                        notify_emails: this.form.notify_emails(),
                        web_confirmation_message: this.form.web_confirmation_message(),
                        email_confirmation_message: this.form.email_confirmation_message(),
                        email_notification_message: this.form.email_notification_message(),
                        email_confirmation_title: this.form.email_confirmation_title(),
                        email_notification_title: this.form.email_notification_title(),
                        automatic_discussion_options: this.form.automatic_discussion_options(),
                        permission_see_own: this.form.permission_see_own(),
                        permission_see_any: this.form.permission_see_any(),
                        permission_edit_own: this.form.permission_edit_own(),
                        permission_edit_any: this.form.permission_edit_any(),
                        show_on_creation: this.form.show_on_creation(),
                    }, {
                        errorHandler: this.onerror.bind(this),
                    }).then(() => {
                        this.loading = false;
                        this.dirty = false;
                        this.savedAt = new Date();
                        this.validationErrors = {};
                        this.history.clear();
                        m.redraw();
                    }).catch(error => {
                        this.loading = false;
                        m.redraw();
                        throw error;
                    });
                },
                floating: true,
            }),
        ]);
    }

    templateTab() {
        const expertMode = localStorage.getItem('formulaireExpertMode') === 'true';

        const list = new FieldList({
            configs: this.history.value,
            onchange: () => {
                this.history.save();
                this.dirty = true;
            },
            disabled: this.loading,
            validationErrors: this.validationErrors.template || {},
            expertMode,
        });

        return [
            FormGroup.component({
                validationErrors: this.validationErrors.title,
                label: m('label', app.translator.trans(translationPrefix + 'field.title')),
            }, m('input.FormControl', {
                type: 'text',
                value: this.form.title(),
                oninput: event => {
                    this.updateAttribute('title', event.target.value);
                },
                disabled: this.loading,
            })),
            m('h2', [
                app.translator.trans(translationPrefix + 'section.template'),
                ' ',
                m('small', [
                    m('a', {
                        href: app.route.formulaireFormShow(this.form),
                        target: '_blank',
                    }, app.translator.trans(translationPrefix + (this.dirty ? 'save-for-preview' : 'preview'))),
                    ' ',
                    icon('fas fa-external-link-alt'),
                ]),
            ]),
            expertMode ? m('.Form-group', m('.Alert.Alert--warning', app.translator.trans(translationPrefix + 'expert-mode-warning'))) : null,
            list.configureView(),
        ];
    }

    placementTab() {
        const linkIdOptions = {};

        if (this.form.link_type() === 'groups') {
            linkIdOptions.all = app.translator.trans(translationPrefix + 'link-options.all-groups');
            app.store.all('groups').forEach(group => {
                if (group.id() === Group.GUEST_ID || group.id() === Group.MEMBER_ID) {
                    return;
                }

                linkIdOptions[group.id()] = group.namePlural();
            });
        } else if (this.form.link_type() === 'tags') {
            linkIdOptions.all = app.translator.trans(translationPrefix + 'link-options.all-tags');
            if (flarum.extensions['flarum-tags']) {
                app.store.all('tags').forEach(tag => {
                    linkIdOptions[tag.id()] = tag.name();
                });
            }
        }

        return [
            FormGroup.component({
                validationErrors: this.validationErrors.link_type || this.validationErrors.link_id,
                label: m('label', app.translator.trans(translationPrefix + 'field.link')),
            }, [
                SelectWithDisabled.component({
                    options: {
                        standalone: app.translator.trans(translationPrefix + 'link-options.standalone'),
                        groups: app.translator.trans(translationPrefix + 'link-options.groups'),
                        tags: app.translator.trans(translationPrefix + 'link-options.tags'),
                    },
                    value: this.form.link_type() || 'standalone',
                    onchange: value => {
                        this.updateAttribute('link_type', value === 'standalone' ? null : value);
                        this.updateAttribute('link_id', null);
                    },
                    disabled: this.loading,
                }),
                ' ',
                this.form.link_type() !== null ? SelectWithDisabled.component({
                    options: linkIdOptions,
                    value: this.form.link_id() || 'all',
                    onchange: value => {
                        this.updateAttribute('link_id', value === 'all' ? null : value);
                    },
                    disabled: this.loading,
                }) : null,
            ]),
            this.form.type() === 'user' ? FormGroup.component({
                validationErrors: this.validationErrors.show_on_creation,
            }, Switch.component({
                state: this.form.show_on_creation(),
                onchange: this.updateAttribute.bind(this, 'show_on_creation'),
                disabled: this.disabled,
            }, app.translator.trans(translationPrefix + 'field.show-on-creation.' + this.form.type()))) : null,
            this.form.type() === 'discussion' ? FormGroup.component({
                validationErrors: this.validationErrors.automatic_discussion_options,
            }, [
                Switch.component({
                    state: this.form.automatic_discussion_options() && this.form.automatic_discussion_options().enabled,
                    onchange: value => {
                        const options = this.form.automatic_discussion_options() || {};
                        options.enabled = value;
                        this.updateAttribute('automatic_discussion_options', options);
                    },
                    disabled: this.disabled,
                }, app.translator.trans(translationPrefix + 'field.automatic-discussion')),
                Button.component({
                    className: 'Button',
                    disabled: !this.form.automatic_discussion_options() || !this.form.automatic_discussion_options().enabled,
                    onclick: () => {
                        app.modal.show(new AutomaticDiscussionOptionsModal({
                            options: this.form.automatic_discussion_options() || {},
                            onchange: options => {
                                this.updateAttribute('automatic_discussion_options', options);
                                app.modal.close();
                            },
                        }));
                    },
                }, app.translator.trans(translationPrefix + 'auto-discussion-options')),
            ]) : null,
        ];
    }

    accessTab() {
        const showMaxSubmissions = this.form.type() === 'standalone' || this.form.isAutoLink();

        return [
            FormGroup.component({
                validationErrors: this.validationErrors.accept_submissions,
            }, Switch.component({
                state: this.form.accept_submissions(),
                onchange: this.updateAttribute.bind(this, 'accept_submissions'),
                disabled: this.disabled,
            }, app.translator.trans(translationPrefix + 'field.accept-submissions'))),
            this.form.type() === 'standalone' ? FormGroup.component({
                validationErrors: this.validationErrors.allow_modification,
            }, Switch.component({
                state: this.form.allow_modification(),
                onchange: this.updateAttribute.bind(this, 'allow_modification'),
                disabled: this.disabled,
            }, app.translator.trans(translationPrefix + 'field.allow-modification'))) : null,
            showMaxSubmissions ? FormGroup.component({
                validationErrors: this.validationErrors.max_submissions,
                label: m('label', app.translator.trans(translationPrefix + 'field.max-submissions')),
            }, m('input.FormControl', {
                type: 'number',
                value: this.form.max_submissions(),
                oninput: event => {
                    this.updateAttribute('max_submissions', event.target.value);
                },
                disabled: this.loading,
            })) : null,
            this.form.type() !== 'standalone' ? [
                m('h2', app.translator.trans(translationPrefix + 'section.permissions')),
                FormGroup.component({
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_see_own,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-see-own')),
                }, PermissionDropdown.component({
                    groupIds: this.form.permission_see_own(),
                    onchange: this.updateAttribute.bind(this, 'permission_see_own'),
                    disabled: this.disabled,
                    allowGuest: this.form.type() === 'user',
                    guestMeansDisabled: true,
                })),
                FormGroup.component({
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_see_any,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-see-any')),
                }, PermissionDropdown.component({
                    groupIds: this.form.permission_see_any(),
                    onchange: this.updateAttribute.bind(this, 'permission_see_any'),
                    disabled: this.disabled,
                    allowGuest: true,
                })),
                FormGroup.component({
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_edit_own,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-edit-own')),
                }, PermissionDropdown.component({
                    groupIds: this.form.permission_edit_own(),
                    onchange: this.updateAttribute.bind(this, 'permission_edit_own'),
                    disabled: this.disabled,
                    allowGuest: this.form.type() === 'user',
                    guestMeansDisabled: true,
                })),
                FormGroup.component({
                    className: 'FormulairePermissionFormGroup',
                    validationErrors: this.validationErrors.permission_edit_any,
                    label: m('label', app.translator.trans(translationPrefix + 'field.permission-edit-any')),
                }, PermissionDropdown.component({
                    groupIds: this.form.permission_edit_any(),
                    onchange: this.updateAttribute.bind(this, 'permission_edit_any'),
                    disabled: this.disabled,
                })),
            ] : null,
        ];
    }

    notificationsTab() {
        return [
            m('.Form-group', [
                Switch.component({
                    state: this.form.send_confirmation_to_participants(),
                    onchange: this.updateAttribute.bind(this, 'send_confirmation_to_participants'),
                    disabled: this.disabled,
                }, app.translator.trans(translationPrefix + 'field.send-confirmation-to-participants')),
                Button.component({
                    className: 'Button',
                    disabled: !this.form.send_confirmation_to_participants(),
                    onclick: () => {
                        app.modal.show(new ConfirmationEmailOptionsModal({
                            title: this.form.email_confirmation_title(),
                            message: this.form.email_confirmation_message(),
                            onchange: (title, message) => {
                                this.updateAttribute('email_confirmation_title', title);
                                this.updateAttribute('email_confirmation_message', message);
                                app.modal.close();
                            },
                        }));
                    },
                }, app.translator.trans(translationPrefix + 'email-confirmation-options')),
            ]),
            FormGroup.component({
                validationErrors: this.validationErrors.notify_emails,
                label: m('label', app.translator.trans(translationPrefix + 'field.notify-emails')),
            }, [
                m('input.FormControl', {
                    type: 'text',
                    value: this.form.notify_emails(),
                    oninput: event => {
                        this.updateAttribute('notify_emails', event.target.value);
                    },
                    disabled: this.loading,
                }),
                Button.component({
                    className: 'Button',
                    disabled: !this.form.notify_emails(),
                    onclick: () => {
                        app.modal.show(new ConfirmationEmailOptionsModal({
                            title: this.form.email_notification_title(),
                            message: this.form.email_notification_message(),
                            onchange: (title, message) => {
                                this.updateAttribute('email_notification_title', title);
                                this.updateAttribute('email_notification_message', message);
                                app.modal.close();
                            },
                        }));
                    },
                }, app.translator.trans(translationPrefix + 'email-notification-options'))
            ]),
            FormGroup.component({
                validationErrors: this.validationErrors.web_confirmation_message,
                label: m('label', app.translator.trans(translationPrefix + 'field.web-confirmation-message')),
            }, RichTextField.component({
                value: this.form.web_confirmation_message(),
                onchange: event => {
                    this.updateAttribute('web_confirmation_message', event.target.value);
                },
                disabled: this.loading,
            })),
        ];
    }

    exportTab() {
        return [
            // TODO: move import/export to popup and translate
            FormGroup.component({
                label: m('label', 'Import/Export (you must refresh the page before copying for the latest version)'),
            }, m('textarea.FormControl', {
                value: JSON.stringify(this.form.template(), null, 4),
                oninput: event => {
                    try {
                        this.history.value = JSON.parse(event.target.value);
                        this.history.save();
                        this.dirty = true;
                    } catch (e) {
                        alert('Invalid JSON');
                    }
                },
                disabled: this.loading,
            })),
        ];
    }

    updateAttribute(attribute, value) {
        // If the title is being edited, and both title and private title were identical, edit both together
        if (attribute === 'title' && this.form.title() === this.form.private_title()) {
            this.form.pushAttributes({
                private_title: value,
            });
        }

        this.form.pushAttributes({
            [attribute]: value,
        });

        this.dirty = true;
        delete this.validationErrors[attribute];
    }

    warnings() {
        const warnings = [];

        const finalPermission = (localAbility, globalAbility) => {
            const local = this.form['permission_' + localAbility]();

            if (local !== null) {
                return local;
            }

            return app.forum.attribute('formulaireGlobalPermissions')[globalAbility];
        };

        function doesntAllowOnly(permissionValue, groupId) {
            return Array.isArray(permissionValue) && permissionValue.indexOf(groupId) === -1;
        }

        if (this.form.type() !== 'standalone' && this.form.show_on_creation()) {
            if (this.form.type() === 'user') {
                const seeOwnPermission = finalPermission('see_own', 'formulaire.seeOwnUser');
                const editOwnPermission = finalPermission('edit_own', 'formulaire.editOwnUser');

                if (doesntAllowOnly(seeOwnPermission, Group.GUEST_ID) || doesntAllowOnly(editOwnPermission, Group.GUEST_ID)) {
                    warnings.push(app.translator.trans(translationPrefix + 'warnings.show-on-signup-invalid-permissions'));
                }
            } else {
                const seeOwnPermission = finalPermission('see_own', 'discussion.seeOwnFormulaire');
                const editOwnPermission = finalPermission('edit_own', 'discussion.editOwnFormulaire');

                if (doesntAllowOnly(seeOwnPermission, Group.MEMBER_ID) || doesntAllowOnly(editOwnPermission, Group.MEMBER_ID)) {
                    warnings.push(app.translator.trans(translationPrefix + 'warnings.show-on-composer-invalid-permissions'));
                }
            }
        }

        if (this.form.type() !== 'standalone' && !this.form.accept_submissions()) {
            warnings.push(app.translator.trans(translationPrefix + 'warnings.linked-form-is-draft'));
        }

        if (this.form.isAutoLink()) {
            warnings.push(app.translator.trans(translationPrefix + 'warnings.auto-link-permission-warning'));
        }

        return warnings;
    }
}
